import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from './contexts/AuthContext';
import { setTokenGetter } from './services/api';
import MainLayout from './layouts/MainLayout';

// Pages
import DashboardPage from './pages/DashboardPage';
import CoursesPage from './pages/CoursesPage';
import CourseDetailPage from './pages/CourseDetailPage';
import ProfilePage from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';
import BadgesPage from './pages/BadgesPage';
import CertificatesPage from './pages/CertificatesPage';
import LeaderboardPage from './pages/LeaderboardPage';
import VerifyPage from './pages/VerifyPage';
import LoginPage from './pages/LoginPage';
import AdminImportPage from './pages/AdminImportPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminModulesPage from './pages/AdminModulesPage';
import AdminModulePermissionsPage from './pages/AdminModulePermissionsPage';
import AdminAssignmentsPage from './pages/AdminAssignmentsPage';
import AdminReleaseNotesPage from './pages/AdminReleaseNotesPage';
import AdminCoursesPage from './pages/AdminCoursesPage';
import DeepLinkPage from './pages/DeepLinkPage';
import QuizPage from './pages/QuizPage';
import LessonPage from './pages/LessonPage';

import ModuleDetailPage from './pages/ModuleDetailPage';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, login } = useAuth();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    login();
    return null;
  }

  return <>{children}</>;
}

function App() {
  const { token } = useAuth();

  useEffect(() => {
    setTokenGetter(() => token);
  }, [token]);

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/verify/:code" element={<VerifyPage />} />
      <Route path="/verify" element={<VerifyPage />} />
      <Route path="/login" element={<LoginPage />} />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="courses" element={<CoursesPage />} />
        <Route path="courses/:courseId" element={<CourseDetailPage />} />
        <Route path="courses/:courseId/modules/:moduleId" element={<ModuleDetailPage />} />
        <Route path="courses/:courseId/lessons/:lessonId" element={<LessonPage />} />
        <Route path="badges" element={<BadgesPage />} />
        <Route path="certificates" element={<CertificatesPage />} />
        <Route path="leaderboard" element={<LeaderboardPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="admin" element={<AdminDashboardPage />} />
        <Route path="admin/courses" element={<AdminCoursesPage />} />
        <Route path="admin/users" element={<AdminUsersPage />} />
        <Route path="admin/modules" element={<AdminModulesPage />} />
        <Route path="admin/modules/:moduleId/permissions" element={<AdminModulePermissionsPage />} />
        <Route path="admin/import" element={<AdminImportPage />} />
        <Route path="admin/assignments" element={<AdminAssignmentsPage />} />
        <Route path="admin/release-notes" element={<AdminReleaseNotesPage />} />

        {/* Quiz route */}
        <Route path="quiz/:quizId" element={<QuizPage />} />

        {/* Deep-link routes */}
        <Route path="learn/courses/:slug" element={<DeepLinkPage />} />
        <Route path="learn/lessons/:slug" element={<DeepLinkPage />} />
        <Route path="learn/:type/:slug" element={<DeepLinkPage />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default App;
